import { ApiClientError, api, unwrap } from "@lumberset/api-client";
import { Button, Heading, Logo, Spinner } from "@lumberset/ui";
import { createFileRoute, type ErrorComponentProps, Link, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Where an Admin's sign-in-as link lands. Redeeming the ticket on this host
 * signs this browser in as the Member for an hour; then it is the Yard app
 * as that person sees it, with a banner saying so.
 */
export const Route = createFileRoute("/impersonate/$ticket")({
  loader: async ({ params, context }) => {
    await unwrap(api.public.impersonations[":id"].redeem.$post({ param: { id: params.ticket } }));
    // Whatever was cached belongs to whoever was signed in here before.
    context.queryClient.clear();
    throw redirect({ to: "/" });
  },
  pendingComponent: () => (
    <Frame>
      <div className="flex items-center gap-x-3">
        <Spinner />
        <p className="txt-small text-ui-fg-subtle">Signing you in as this person.</p>
      </div>
    </Frame>
  ),
  errorComponent: LinkFailed,
  component: () => null,
});

function LinkFailed({ error }: ErrorComponentProps) {
  const message =
    error instanceof ApiClientError
      ? error.message
      : "This link no longer works. Sign in as them again from the Admin panel.";
  return (
    <Frame>
      <Heading>Could not sign you in as this person</Heading>
      <p className="txt-small text-ui-fg-subtle">{message}</p>
      <Button asChild variant="secondary" size="large" block>
        <Link to="/login" search={{ redirect: undefined }}>
          Go to sign in
        </Link>
      </Button>
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="pt-safe pb-safe flex min-h-dvh flex-col items-center justify-center bg-ui-bg-subtle px-4 py-10">
      <Logo className="mb-8 text-[17px]" markClassName="size-7" />
      <div className="flex w-full max-w-[400px] flex-col gap-y-4 rounded-xl bg-ui-bg-base p-6 shadow-elevation-card-rest sm:p-8">
        {children}
      </div>
    </div>
  );
}
